import React, { useEffect, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from './AuthContext';
import '../styles/Ctf.css';

const POSITIONS = [
  { key: 'mam_non', label: 'Mầm Non' },
  { key: 'tap_su', label: 'Tập Sự' },
  { key: 'culi_cap_cao', label: 'Culi Cấp Cao' },
  { key: 'bo_lao', label: 'Bô Lão' }
];
const CATEGORIES = [
  { key: 'web', label: 'Web' },
  { key: 'crypto', label: 'Crypto' },
  { key: 'pwn', label: 'Pwn' },
  { key: 'reverse', label: 'Reverse' },
  { key: 'forensic', label: 'Forensic' },
  { key: 'osint', label: 'OSINT' },
  { key: 'misc', label: 'Misc' }
];
const CAT_COLOR = {
  web: '#3b82f6', crypto: '#a855f7', pwn: '#ef4444', reverse: '#f97316',
  forensic: '#14b8a6', osint: '#eab308', misc: '#6b7280'
};

function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
}

// ===== Main component =====
// mode: 'member' (default) | 'admin'
const Ctf = ({ mode = 'member' }) => {
  const { token } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}` };
  const [view, setView] = useState({ name: 'challenges' });
  const [msg, setMsg] = useState(null);
  const show = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4500); };

  return (
    <div className="ctf-root">
      {msg && <div className={`ctf-msg ${msg.type}`}>{msg.text}</div>}

      <div className="ctf-tabbar">
        <button className={`ctf-navbtn ${view.name === 'challenges' ? 'active' : ''}`}
          onClick={() => setView({ name: 'challenges' })}>Thử Thách</button>
        <button className={`ctf-navbtn ${view.name === 'scoreboard' ? 'active' : ''}`}
          onClick={() => setView({ name: 'scoreboard' })}>Bảng Xếp Hạng</button>
        {mode === 'admin' && (
          <button className={`ctf-navbtn ${view.name === 'writeups' ? 'active' : ''}`}
            onClick={() => setView({ name: 'writeups' })}>Duyệt Writeup</button>
        )}
      </div>

      {view.name === 'challenges' && (
        <ChallengeList mode={mode} authHeaders={authHeaders} show={show}
          openChallenge={(id) => setView({ name: 'challenge', id })} token={token} />
      )}
      {view.name === 'challenge' && (
        <ChallengeView id={view.id} mode={mode} authHeaders={authHeaders} show={show} token={token}
          back={() => setView({ name: 'challenges' })} />
      )}
      {view.name === 'scoreboard' && (
        <Scoreboard mode={mode} authHeaders={authHeaders} show={show} />
      )}
      {view.name === 'writeups' && mode === 'admin' && (
        <WriteupReview authHeaders={authHeaders} show={show} />
      )}
    </div>
  );
};

// ===== Challenge list + admin create/edit =====
const ChallengeList = ({ mode, authHeaders, show, openChallenge, token }) => {
  const [challenges, setChallenges] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [catFilter, setCatFilter] = useState('');

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/ctf/challenges', { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setChallenges(data.challenges || []);
      setIsAdmin(!!data.isAdmin);
    } catch (err) { show('error', err.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const onDelete = async (id) => {
    if (!window.confirm('Xoá thử thách này? Mọi kết quả của thành viên cũng sẽ bị xoá.')) return;
    try {
      const res = await fetch(`/api/ctf/challenges/${id}`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      show('success', 'Đã xoá thử thách.');
      refresh();
    } catch (err) { show('error', err.message); }
  };

  const startEdit = async (id) => {
    try {
      const res = await fetch(`/api/ctf/challenges/${id}`, { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setEditing(data.challenge);
      setShowForm(true);
    } catch (err) { show('error', err.message); }
  };

  const filtered = catFilter ? challenges.filter(c => c.category === catFilter) : challenges;

  if (showForm) {
    return <ChallengeForm editing={editing} authHeaders={authHeaders} show={show}
      onDone={() => { setShowForm(false); setEditing(null); refresh(); }}
      onCancel={() => { setShowForm(false); setEditing(null); }} />;
  }

  return (
    <div>
      <div className="ctf-toolbar">
        <div className="ctf-cat-filters">
          <button className={`ctf-chip ${catFilter === '' ? 'on' : ''}`} onClick={() => setCatFilter('')}>Tất cả</button>
          {CATEGORIES.map(c => (
            <button key={c.key} className={`ctf-chip ${catFilter === c.key ? 'on' : ''}`}
              style={catFilter === c.key ? { background: CAT_COLOR[c.key], borderColor: CAT_COLOR[c.key] } : {}}
              onClick={() => setCatFilter(c.key)}>{c.label}</button>
          ))}
        </div>
        {mode === 'admin' && isAdmin && (
          <button className="btn btn-primary btn-sm" onClick={() => { setEditing(null); setShowForm(true); }}>
            + Tạo thử thách
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <p className="ctf-muted">Chưa có thử thách nào.</p>
      ) : (
        <div className="ctf-grid">
          {filtered.map(ch => (
            <div key={ch.id} className={`ctf-card ${ch.solveStatus === 'full' ? 'solved' : ''}`}>
              <div className="ctf-card-top">
                <span className="ctf-cat-tag" style={{ background: CAT_COLOR[ch.category] || '#6b7280' }}>{ch.category}</span>
                <span className="ctf-diff">{ch.difficulty}</span>
              </div>
              <h4 className="ctf-card-title" onClick={() => openChallenge(ch.id)}>{ch.title}</h4>
              <div className="ctf-card-meta">
                <span className="ctf-points">{ch.points} đ</span>
                <span className="ctf-solvers">{ch.solverCount} giải</span>
              </div>
              {ch.solveStatus === 'full' && <span className="ctf-solved-badge">✓ Hoàn thành</span>}
              {ch.solveStatus === 'half' && <span className="ctf-half-badge">◐ Chờ duyệt writeup</span>}
              <div className="ctf-card-actions">
                <button className="btn btn-outline btn-sm" onClick={() => openChallenge(ch.id)}>Mở</button>
                {mode === 'admin' && isAdmin && (
                  <>
                    <button className="btn btn-outline btn-sm" onClick={() => startEdit(ch.id)}>Sửa</button>
                    <button className="btn btn-danger btn-sm" onClick={() => onDelete(ch.id)}>Xoá</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== Admin challenge create/edit form =====
const ChallengeForm = ({ editing, authHeaders, show, onDone, onCancel }) => {
  const [form, setForm] = useState(() => editing ? {
    title: editing.title || '',
    description: editing.description || '',
    category: editing.category || 'misc',
    difficulty: editing.difficulty || 'easy',
    points: editing.points || 100,
    flag: editing.flag || '',
    deliveryType: editing.deliveryType || 'text',
    link: editing.link || '',
    contentText: editing.contentText || '',
    allowedPositions: editing.allowedPositions || [],
    requireWriteup: !!editing.requireWriteup,
    hints: Array.isArray(editing.hints) ? editing.hints.map(h => ({ text: h.text || '', cost: h.cost || 0 })) : []
  } : {
    title: '', description: '', category: 'misc', difficulty: 'easy', points: 100,
    flag: '', deliveryType: 'text', link: '', contentText: '',
    allowedPositions: [], requireWriteup: false, hints: []
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const togglePos = (key) => {
    setForm(f => ({
      ...f,
      allowedPositions: f.allowedPositions.includes(key)
        ? f.allowedPositions.filter(p => p !== key)
        : [...f.allowedPositions, key]
    }));
  };
  const addHint = () => setForm(f => ({ ...f, hints: [...f.hints, { text: '', cost: 0 }] }));
  const updateHint = (i, k, v) => setForm(f => ({ ...f, hints: f.hints.map((h, idx) => idx === i ? { ...h, [k]: v } : h) }));
  const removeHint = (i) => setForm(f => ({ ...f, hints: f.hints.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return show('error', 'Nhập tên thử thách.');
    if (!form.flag.trim()) return show('error', 'Nhập flag (đáp án).');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('description', form.description);
      fd.append('category', form.category);
      fd.append('difficulty', form.difficulty);
      fd.append('points', String(parseInt(form.points) || 0));
      fd.append('flag', form.flag);
      fd.append('deliveryType', form.deliveryType);
      fd.append('link', form.link);
      fd.append('contentText', form.contentText);
      fd.append('allowedPositions', JSON.stringify(form.allowedPositions));
      fd.append('requireWriteup', form.requireWriteup ? 'true' : 'false');
      fd.append('hints', JSON.stringify(form.hints.filter(h => h.text.trim())));
      if (form.deliveryType === 'file' && file) fd.append('file', file);

      const url = editing ? `/api/ctf/challenges/${editing.id}` : '/api/ctf/challenges';
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      show('success', editing ? 'Đã cập nhật thử thách.' : 'Đã tạo thử thách.');
      onDone();
    } catch (err) { show('error', err.message); }
    finally { setBusy(false); }
  };

  return (
    <form className="ctf-form" onSubmit={submit}>
      <div className="ctf-form-head">
        <h3>{editing ? 'Sửa thử thách' : 'Tạo thử thách mới'}</h3>
        <button type="button" className="btn btn-outline btn-sm" onClick={onCancel}>Đóng</button>
      </div>

      <div className="ctf-form-grid">
        <div className="form-group ctf-full">
          <label>Tên thử thách *</label>
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Thể loại</label>
          <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
            {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Mức độ (tự nhập: easy, medium, hard, wtf...)</label>
          <input value={form.difficulty} onChange={e => setForm({ ...form, difficulty: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Điểm</label>
          <input type="number" min="0" value={form.points} onChange={e => setForm({ ...form, points: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Flag (đáp án) *</label>
          <input value={form.flag} onChange={e => setForm({ ...form, flag: e.target.value })} placeholder="ISP{...}" />
        </div>
        <div className="form-group ctf-full">
          <label>Mô tả thử thách (Markdown)</label>
          <textarea rows={5} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
        </div>

        <div className="form-group ctf-full">
          <label>Dạng đề</label>
          <div className="ctf-radio-row">
            {['text', 'file', 'link'].map(t => (
              <label key={t} className="ctf-radio">
                <input type="radio" name="deliveryType" checked={form.deliveryType === t}
                  onChange={() => setForm({ ...form, deliveryType: t })} />
                {t === 'text' ? 'Text' : t === 'file' ? 'File' : 'Link'}
              </label>
            ))}
          </div>
        </div>
        {form.deliveryType === 'file' && (
          <div className="form-group ctf-full">
            <label>File thử thách {editing && editing.hasFile ? `(hiện tại: ${editing.originalName})` : ''}</label>
            <input type="file" onChange={e => setFile(e.target.files[0])} />
          </div>
        )}
        {form.deliveryType === 'link' && (
          <div className="form-group ctf-full">
            <label>Link thử thách</label>
            <input value={form.link} onChange={e => setForm({ ...form, link: e.target.value })} placeholder="http://..." />
          </div>
        )}
        {form.deliveryType === 'text' && (
          <div className="form-group ctf-full">
            <label>Nội dung text (đề bài / dữ liệu)</label>
            <textarea rows={4} value={form.contentText} onChange={e => setForm({ ...form, contentText: e.target.value })} />
          </div>
        )}

        <div className="form-group ctf-full">
          <label>Nhóm member được xem & giải</label>
          <div className="ctf-checkbox-row">
            {POSITIONS.map(p => (
              <label key={p.key} className="ctf-checkbox">
                <input type="checkbox" checked={form.allowedPositions.includes(p.key)} onChange={() => togglePos(p.key)} />
                {p.label}
              </label>
            ))}
          </div>
        </div>

        <div className="form-group ctf-full">
          <label className="ctf-checkbox">
            <input type="checkbox" checked={form.requireWriteup}
              onChange={e => setForm({ ...form, requireWriteup: e.target.checked })} />
            Yêu cầu nộp writeup (flag đúng được 1/2 điểm, 1/2 còn lại do admin duyệt writeup)
          </label>
        </div>

        <div className="form-group ctf-full">
          <div className="ctf-hints-head">
            <label>Gợi ý (hint)</label>
            <button type="button" className="btn btn-outline btn-sm" onClick={addHint}>+ Thêm hint</button>
          </div>
          {form.hints.map((h, i) => (
            <div key={i} className="ctf-hint-row">
              <input className="ctf-hint-text" placeholder={`Nội dung hint #${i + 1}`} value={h.text}
                onChange={e => updateHint(i, 'text', e.target.value)} />
              <input className="ctf-hint-cost" type="number" min="0" placeholder="Chi phí" value={h.cost}
                onChange={e => updateHint(i, 'cost', e.target.value)} />
              <button type="button" className="btn btn-danger btn-sm" onClick={() => removeHint(i)}>X</button>
            </div>
          ))}
        </div>
      </div>

      <div className="ctf-form-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Đang lưu...' : (editing ? 'Lưu' : 'Tạo')}</button>
        <button type="button" className="btn btn-outline" onClick={onCancel}>Huỷ</button>
      </div>
    </form>
  );
};

// ===== Member/Admin challenge detail =====
const ChallengeView = ({ id, mode, authHeaders, show, token, back }) => {
  const [ch, setCh] = useState(null);
  const [solve, setSolve] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [myScore, setMyScore] = useState(null);
  const [flag, setFlag] = useState('');
  const [writeup, setWriteup] = useState('');
  const [busy, setBusy] = useState(false);
  const [solvers, setSolvers] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`/api/ctf/challenges/${id}`, { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setCh(data.challenge);
      setSolve(data.solve);
      setIsAdmin(!!data.isAdmin);
      setMyScore(data.myScore);
      if (data.solve && data.solve.writeup) setWriteup(data.solve.writeup);
    } catch (err) { show('error', err.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => { refresh(); }, [refresh]);

  const unlockHint = async (hintId) => {
    if (!window.confirm('Mở hint này sẽ trừ điểm theo chi phí. Tiếp tục?')) return;
    try {
      const res = await fetch(`/api/ctf/challenges/${id}/hint/${hintId}`, { method: 'POST', headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      show('success', 'Đã mở hint.');
      refresh();
    } catch (err) { show('error', err.message); }
  };

  const submitFlag = async (e) => {
    e.preventDefault();
    if (!flag.trim()) return show('error', 'Nhập flag.');
    setBusy(true);
    try {
      const res = await fetch(`/api/ctf/challenges/${id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ flag, writeup })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message);
      if (data.correct) { show('success', data.message); setFlag(''); refresh(); }
      else show('error', data.message || 'Flag sai.');
    } catch (err) { show('error', err.message); }
    finally { setBusy(false); }
  };

  const loadSolvers = async () => {
    try {
      const res = await fetch(`/api/ctf/scoreboard`, { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSolvers(data.scoreboard || []);
    } catch (err) { show('error', err.message); }
  };

  const deleteResult = async (userId) => {
    if (!window.confirm('Xoá kết quả của thành viên này (gồm cả điểm đã cộng) để họ giải lại?')) return;
    try {
      const res = await fetch(`/api/ctf/challenges/${id}/solves/${userId}`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      show('success', 'Đã xoá kết quả.');
      refresh();
    } catch (err) { show('error', err.message); }
  };

  if (!ch) return <p className="ctf-muted">Đang tải...</p>;
  const fullySolved = solve && solve.status === 'full';

  return (
    <div className="ctf-detail">
      <button className="btn btn-outline btn-sm" onClick={back}>← Quay lại</button>
      <div className="ctf-detail-head">
        <span className="ctf-cat-tag" style={{ background: CAT_COLOR[ch.category] || '#6b7280' }}>{ch.category}</span>
        <h2>{ch.title}</h2>
        <span className="ctf-diff">{ch.difficulty}</span>
        <span className="ctf-points big">{ch.points} đ</span>
      </div>
      {myScore && <p className="ctf-muted">Điểm CTF của bạn: <strong>{myScore.net}</strong> (đã trừ {myScore.hintCost} chi phí hint)</p>}

      <div className="ctf-statement ctf-md">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{ch.description || '*Không có mô tả.*'}</ReactMarkdown>
      </div>

      {/* Delivery */}
      {ch.deliveryType === 'file' && ch.hasFile && (
        <p><a className="btn btn-outline btn-sm" href={`/api/ctf/challenges/${ch.id}/download?token=${encodeURIComponent(token)}`}>
          ⬇ Tải file: {ch.originalName} {ch.fileSize ? `(${fmtSize(ch.fileSize)})` : ''}
        </a></p>
      )}
      {ch.deliveryType === 'link' && ch.link && (
        <p>🔗 <a href={ch.link} target="_blank" rel="noreferrer">{ch.link}</a></p>
      )}
      {ch.deliveryType === 'text' && ch.contentText && (
        <pre className="ctf-text-content">{ch.contentText}</pre>
      )}

      {/* Hints */}
      {ch.hints && ch.hints.length > 0 && (
        <div className="ctf-hints">
          <h4>Gợi ý</h4>
          {ch.hints.map(h => (
            <div key={h.id} className="ctf-hint-item">
              {h.unlocked ? (
                <span className="ctf-hint-revealed">💡 {h.text}</span>
              ) : (
                <button className="btn btn-outline btn-sm" onClick={() => unlockHint(h.id)}>
                  Mở hint (−{h.cost} điểm)
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Submit (members) */}
      {!isAdmin && (
        fullySolved ? (
          <div className="ctf-solved-box">✓ Bạn đã hoàn thành thử thách này.</div>
        ) : (
          <form className="ctf-submit" onSubmit={submitFlag}>
            {solve && solve.status === 'half' && (
              <div className="ctf-half-box">◐ Flag đã đúng (+{solve.flagScore} điểm). Writeup đang chờ admin duyệt để cộng nốt điểm còn lại.</div>
            )}
            <div className="form-group">
              <label>Nộp flag</label>
              <input value={flag} onChange={e => setFlag(e.target.value)} placeholder="ISP{...}" />
            </div>
            {ch.requireWriteup && (
              <div className="form-group">
                <label>Writeup (bắt buộc — Markdown)</label>
                <textarea rows={6} value={writeup} onChange={e => setWriteup(e.target.value)}
                  placeholder="Trình bày cách giải của bạn..." />
              </div>
            )}
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Đang nộp...' : 'Nộp flag'}</button>
          </form>
        )
      )}

      {/* Admin: solvers + delete result */}
      {isAdmin && (
        <div className="ctf-admin-solvers">
          <h4>Quản lý kết quả</h4>
          {!solvers ? (
            <button className="btn btn-outline btn-sm" onClick={loadSolvers}>Xem danh sách thành viên đã giải</button>
          ) : (
            <table className="ctf-table">
              <thead><tr><th>Thành viên</th><th>Điểm CTF</th><th>Hành động</th></tr></thead>
              <tbody>
                {solvers.map(s => (
                  <tr key={s.userId}>
                    <td>{s.userSnapshot?.fullName || s.userSnapshot?.username}</td>
                    <td>{s.score}</td>
                    <td><button className="btn btn-danger btn-sm" onClick={() => deleteResult(s.userId)}>Xoá kết quả bài này</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

// ===== Scoreboard =====
const Scoreboard = ({ mode, authHeaders, show }) => {
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/ctf/scoreboard', { headers: authHeaders });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        setRows(data.scoreboard || []);
      } catch (err) { show('error', err.message); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openMember = async (userId) => {
    try {
      const res = await fetch(`/api/ctf/members/${userId}`, { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setDetail(data);
    } catch (err) { show('error', err.message); }
  };

  return (
    <div>
      <table className="ctf-table ctf-scoreboard">
        <thead><tr><th>#</th><th>Thành viên</th><th>Đã giải</th><th>Chờ duyệt</th><th>Điểm</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.userId} className={`rank-${i + 1}`}>
              <td>{i + 1}</td>
              <td><button className="ctf-link" onClick={() => openMember(r.userId)}>{r.userSnapshot?.fullName || r.userSnapshot?.username}</button></td>
              <td>{r.solved}</td>
              <td>{r.half}</td>
              <td><strong>{r.score}</strong></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5} className="ctf-muted">Chưa có dữ liệu.</td></tr>}
        </tbody>
      </table>

      {detail && (
        <div className="ctf-modal-backdrop" onClick={() => setDetail(null)}>
          <div className="ctf-modal" onClick={e => e.stopPropagation()}>
            <div className="ctf-modal-head">
              <h3>{detail.user?.fullName || detail.user?.username}</h3>
              <button className="btn btn-outline btn-sm" onClick={() => setDetail(null)}>Đóng</button>
            </div>
            <p className="ctf-muted">
              Điểm: <strong>{detail.score}</strong> · Flag đúng: {detail.correctAttempts} lần · Flag sai: {detail.wrongAttempts} lần
            </p>
            <h4>Thử thách đã giải</h4>
            {detail.solved.length === 0 ? <p className="ctf-muted">Chưa giải bài nào.</p> : (
              <table className="ctf-table">
                <thead><tr><th>Tên</th><th>Thể loại</th><th>Trạng thái</th><th>Điểm</th></tr></thead>
                <tbody>
                  {detail.solved.map(s => (
                    <tr key={s.challengeId}>
                      <td>{s.title}</td>
                      <td>{s.category}</td>
                      <td>{s.status === 'full' ? '✓ Hoàn thành' : '◐ 1/2 (chờ writeup)'}</td>
                      <td>{(s.flagScore || 0) + (s.writeupScore || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ===== Admin writeup review =====
const WriteupReview = ({ authHeaders, show }) => {
  const [writeups, setWriteups] = useState([]);
  const [open, setOpen] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/ctf/writeups', { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setWriteups(data.writeups || []);
    } catch (err) { show('error', err.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const review = async (solveId, approve) => {
    try {
      const res = await fetch(`/api/ctf/solves/${solveId}/writeup-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ approve })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      show('success', approve ? 'Đã duyệt & cộng điểm writeup.' : 'Đã từ chối writeup.');
      setOpen(null);
      refresh();
    } catch (err) { show('error', err.message); }
  };

  return (
    <div>
      <table className="ctf-table">
        <thead><tr><th>Thử thách</th><th>Thành viên</th><th>Trạng thái</th><th>Điểm</th><th></th></tr></thead>
        <tbody>
          {writeups.map(w => (
            <tr key={w.solveId}>
              <td>{w.challengeTitle}</td>
              <td>{w.user?.fullName || w.user?.username}</td>
              <td>{w.status === 'half' ? '◐ Chờ duyệt' : (w.writeupApproved ? '✓ Đã duyệt' : '✗ Đã từ chối')}</td>
              <td>{w.flagScore} + {w.writeupScore}</td>
              <td><button className="btn btn-outline btn-sm" onClick={() => setOpen(w)}>Xem writeup</button></td>
            </tr>
          ))}
          {writeups.length === 0 && <tr><td colSpan={5} className="ctf-muted">Không có writeup nào.</td></tr>}
        </tbody>
      </table>

      {open && (
        <div className="ctf-modal-backdrop" onClick={() => setOpen(null)}>
          <div className="ctf-modal" onClick={e => e.stopPropagation()}>
            <div className="ctf-modal-head">
              <h3>Writeup: {open.challengeTitle}</h3>
              <button className="btn btn-outline btn-sm" onClick={() => setOpen(null)}>Đóng</button>
            </div>
            <p className="ctf-muted">Người nộp: {open.user?.fullName || open.user?.username}</p>
            <div className="ctf-statement ctf-md">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{open.writeup || ''}</ReactMarkdown>
            </div>
            <div className="ctf-form-actions">
              <button className="btn btn-primary" onClick={() => review(open.solveId, true)}>Duyệt & cộng điểm</button>
              <button className="btn btn-danger" onClick={() => review(open.solveId, false)}>Từ chối</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ctf;
